import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import AppBar from 'material-ui/AppBar';
import Tabs from 'material-ui/Tabs';
import Tab from 'material-ui/Tabs/Tab';
import { browserHistory } from 'react-router';

import EmptyContent from '../../common/EmptyState/Content';
import HorizontalCard from '../Components/HorizontalCard';
import CommunitiesList from '../Components/CommunitiesList';
import { useLazyLoad } from '../../../hocs/useLazyLoad';
import { isDesktop } from '../../../utils/mobileHelpers';
import { COLORS } from '../../../constants/styles';
import { enhance } from './enhance';
import Loader from '../../common/Loader/index';
import {
  COMMUNITIES_COMMON,
  COMMUNITIES_EMPTY,
  COMMUNITIES_TITLE_MEMBER,
  COMMUNITIES_TITLE_MODERATOR,
} from '../../../constants/strings';
import { COMMUNITIES } from '../../../constants/routes';
import { IDS } from '../../../constants/ids';

const TABS = {
  MEMBER: 'member',
  MODERATOR: 'moderator',
  REQUESTS: 'requests',
};

const MyCommunities = ({
  moderatorCommunitiesCounter: moderatorCounter,
  moderatorCommunitiesList: moderatorList,
  memberCommunitiesCounter: memberCounter,
  memberCommunitiesList: memberList,
  usersJoinRequestsList: requests,
  usersJoinRequestsCounter: requestsCounter,
  usersJoinReq,
  sendingRequests,
  removeNotifications,
  communitiesList,
  loadNextRequests,
  loadNextUserRequests,
  intl: { formatMessage },
  classes,
}) => {
  const rootRef = useRef();
  const [tab, setTab] = useState(TABS.MEMBER);
  const tabs = useTabs(
    formatMessage,
    memberCounter,
    moderatorCounter,
    requestsCounter,
  );
  const list = useList(tab, memberList, moderatorList, requests);
  const hasList = memberCounter || moderatorCounter || requestsCounter;
  const loadMore = useCallback(() => {
    if (tab === 'requests') {
      loadNextUserRequests({ limit: 10 });
    } else {
      loadNextRequests({ type: tab });
    }
  }, [tab]);
  useLazyLoad({ node: rootRef, loadMore, path: ['parentNode'] });

  useEffect(() => {
    communitiesList({ limit: 20 });
    usersJoinReq({ limit: 20 });
  }, []);

  /** Choose default active tab */
  useEffect(() => {
    if (memberCounter === 0) {
      handleTabChange(null, TABS.MODERATOR);
    }

    if (moderatorCounter === 0) {
      handleTabChange(null, TABS.MEMBER);
    }

    if (memberCounter === 0 && moderatorCounter === 0) {
      handleTabChange(null, TABS.REQUESTS);
    }
  }, [moderatorCounter, memberCounter, sendingRequests]);

  const handleTabChange = (e, value) => {
    if (value !== tab) {
      setTab(value);
    }
  };

  const handleSelect = id => {
    if (tab !== 'requests') {
      removeNotifications({ type: 'communities', community_ids: [id] });
    }
    const route = `/${COMMUNITIES}/${id}`;
    browserHistory.push(route);
  };

  return (
    <div styleName="wrapper" className="flex flex-column" ref={rootRef}>
      <Choose>
        <When condition={!sendingRequests}>
          <If condition={hasList}>
            <Fragment>
              <AppBar
                position="static"
                color="default"
                style={{
                  background: 'transparent',
                  border: 0,
                  boxShadow: 'none',
                  marginLeft: isDesktop() && '-56px',
                  marginTop: 16,
                }}
              >
                <Tabs
                  indicatorColor={COLORS.bluePrimary}
                  onChange={handleTabChange}
                  value={tab}
                  scrollable
                >
                  {tabs.map(({ id, counter, label, tab: tabValue }) => (
                    <If condition={!!counter}>
                      <Tab
                        label={`${label} (${counter})`}
                        key={id}
                        id={id}
                        value={tabValue}
                        classes={{
                          root: classes.root,
                          label: classes.label,
                        }}
                      />
                    </If>
                  ))}
                </Tabs>
              </AppBar>
              <CommunitiesList key={tab}>
                {list.map(community => {
                  const {
                    id,
                    banner = {},
                    logo = {},
                    name,
                    organization = {},
                    totalPosts,
                    totalUsers,
                    communityMemberChatroom = {},
                  } = community.community || community || {};
                  return (
                    <HorizontalCard
                      key={id}
                      banner={banner.url}
                      logo={logo.url}
                      name={name}
                      organization={organization.name}
                      totalPosts={totalPosts}
                      totalUsers={totalUsers}
                      onClick={() => handleSelect(id)}
                      counter={communityMemberChatroom.totalUnreadCount}
                    />
                  );
                })}
              </CommunitiesList>
            </Fragment>
            <Else />
            <div styleName="empty-state">
              <EmptyContent
                text={formatMessage(COMMUNITIES_EMPTY)}
                icon="icon-megaphone"
              />
            </div>
          </If>
        </When>
        <Otherwise>
          <Loader />
        </Otherwise>
      </Choose>
    </div>
  );
};

MyCommunities.propTypes = {
  communitiesList: PropTypes.func.isRequired,
  memberCommunitiesCounter: PropTypes.number,
  moderatorCommunitiesCounter: PropTypes.number,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  classes: PropTypes.object.isRequired,
  sendingRequests: PropTypes.bool,
  memberCommunitiesList: PropTypes.arrayOf(PropTypes.object),
  moderatorCommunitiesList: PropTypes.arrayOf(PropTypes.object),
  loadNextRequests: PropTypes.func.isRequired,
  removeNotifications: PropTypes.func.isRequired,
  usersJoinReq: PropTypes.func.isRequired,
  loadNextUserRequests: PropTypes.func.isRequired,
  usersJoinRequestsList: PropTypes.arrayOf(PropTypes.object),
  usersJoinRequestsCounter: PropTypes.number,
};

MyCommunities.defaultProps = {
  memberCommunitiesCounter: 0,
  moderatorCommunitiesCounter: 0,
  usersJoinRequestsCounter: 0,
  sendingRequests: false,
  memberCommunitiesList: [],
  moderatorCommunitiesList: [],
  usersJoinRequestsList: [],
};

export default enhance(MyCommunities);

function useTabs(
  formatMessage,
  memberCounter,
  moderatorCounter,
  requestsCounter,
) {
  return useMemo(
    () => [
      {
        label: formatMessage(COMMUNITIES_TITLE_MEMBER),
        id: IDS.MY_COMMUNITIES_TABS.ALL,
        counter: memberCounter,
        tab: TABS.MEMBER,
      },
      {
        label: formatMessage(COMMUNITIES_TITLE_MODERATOR),
        id: IDS.MY_COMMUNITIES_TABS.I_AM_MODERATOR,
        counter: moderatorCounter,
        tab: TABS.MODERATOR,
      },
      {
        label: formatMessage(COMMUNITIES_COMMON.requested),
        id: IDS.MY_COMMUNITIES_TABS.REQUESTED,
        counter: requestsCounter,
        tab: TABS.REQUESTS,
      },
    ],
    [memberCounter, moderatorCounter, requestsCounter],
  );
}

function useList(tab, memberList, moderatorList, requests) {
  return useMemo(() => {
    switch (tab) {
      case TABS.MEMBER:
        return memberList;
      case TABS.MODERATOR:
        return moderatorList;
      case TABS.REQUESTS:
        return requests;
      default:
        return [];
    }
  }, [tab, memberList, moderatorList, requests]);
}



// WEBPACK FOOTER //
// ./src/components/Communities/MyCommunities/index.jsx